import { Component, ReactNode } from 'react';
import { captureError } from '@shared/lib/observability';

interface Props {
  name?: string;
  children: ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Per-panel error boundary. Root boundary still wraps the whole app;
 * this lets a single section fail without taking the app down.
 * Ported from index.html:1945.
 */
export class SectionErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[SectionErrorBoundary:${this.props.name ?? 'unnamed'}]`, error, info);
    captureError(error, {
      boundary: this.props.name ?? 'unnamed',
      componentStack: info.componentStack ?? null,
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        style={{
          padding: 16,
          borderRadius: 8,
          border: '1px solid rgba(231,76,60,0.35)',
          background: 'rgba(231,76,60,0.08)',
          color: '#e74c3c',
          fontSize: 'var(--fs-body)',
        }}
      >
        <div style={{ marginBottom: 8, fontWeight: 'var(--fw-semibold)' }}>
          Couldn't load {this.props.name ?? 'this section'}
        </div>
        <pre style={{ color: 'var(--dim)', fontSize: 'var(--fs-caption)', whiteSpace: 'pre-wrap', marginBottom: 10 }}>
          {String(this.state.error)}
        </pre>
        <button
          onClick={() => this.setState({ hasError: false, error: null })}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: '1px solid rgba(15,45,100,0.5)',
            background: 'none',
            color: '#ccc',
            cursor: 'pointer',
            fontSize: 'var(--fs-small)',
          }}
        >
          Retry
        </button>
      </div>
    );
  }
}
